/**
 * Every setting a site carries, and the two functions that read and write them.
 *
 * Pure — no React, no fetch — so `node --test` can load it and the renderer and a future editor
 * read one list. The vault note "Site Builder — Display Settings Schema" is the prose version.
 *
 * WHERE EACH LEVEL IS STORED. Nothing here adds a column:
 *   - SITE    the Site item's `custom_fields.Apps` entry for `site`, under `config`, by group
 *   - PAGE    the Page item's site View, `displayConfig.frame`
 *   - SECTION each module, under its `section` key (dashboards ignore it; `band` is theirs)
 *   - MODULE  each module, beside the keys dashboards already use
 *
 * THE RULES, which are what keep a hundred sites upgradeable together:
 *   1. A new setting is one entry whose default reproduces today's rendering.
 *   2. A key is never reused with a new meaning.
 *   3. Only what differs from the default is stored, so a better default reaches every site.
 *   4. Values are names, never CSS. Hex colours appear only in the site theme.
 *   5. An unknown key is dropped and an invalid value falls back to its default — never a throw,
 *      so one bad hand edit cannot blank a page.
 *
 * `auto` marks a default worked out from the content at render time; it is never stored.
 */

export const SETTINGS_VERSION = 1;

export const SITE_SCHEMA = Object.freeze({
  theme: [
    { key: 'primary', type: 'color', default: '#5b5bd6', label: 'Primary colour' },
    { key: 'accent', type: 'color', default: 'auto', auto: 'same as primary', label: 'Accent colour' },
    { key: 'surface', type: 'color', default: '#ffffff', label: 'Surface' },
    { key: 'ink', type: 'color', default: '#1d1d27', label: 'Ink' },
    { key: 'font_heading', type: 'enum', values: ['Inter', 'Sen', 'Georgia', 'system-ui'], default: 'system-ui', label: 'Heading font' },
    { key: 'font_body', type: 'enum', values: ['Inter', 'Sen', 'Georgia', 'system-ui'], default: 'system-ui', label: 'Body font' },
    { key: 'radius', type: 'enum', values: ['none', 'sm', 'md', 'lg'], default: 'md', label: 'Corner radius' },
  ],
  chrome: [
    // `surface`: the page's own colour with a hairline under it, for a site that keeps its brand
    // colour for accents rather than a bar across the top.
    { key: 'header_style', type: 'enum', values: ['solid', 'transparent', 'surface'], default: 'solid', label: 'Header style' },
    { key: 'sticky_header', type: 'bool', default: true, label: 'Sticky header' },
    // The footer's colour, in the section tones' words: `inverse` is the theme's ink, as every footer
    // was; `primary` and `accent` are those colours, deepened until their text reads; `plain` is the
    // page's own colour with a hairline above.
    { key: 'footer_tone', type: 'enum', values: ['inverse', 'primary', 'accent', 'plain'], default: 'inverse', label: 'Footer colour' },
    // The logo in the footer: as it is, or drawn as a white silhouette, for a dark logo on a dark footer
    // (a site rarely has a second, light logo to hand).
    { key: 'footer_logo', type: 'enum', values: ['original', 'white'], default: 'original', label: 'Footer logo' },
    // Where a link to a file goes — a PDF or a picture, wherever on the site the link is. `viewer` opens
    // it over the page (SiteFileViewer); `tab` is a new tab, as every file opened before. A button may
    // say otherwise for itself (`open` on the link, siteModel.resolveLink).
    { key: 'file_links', type: 'enum', values: ['tab', 'viewer'], default: 'tab', label: 'Files open' },
  ],
  detail: [
    // Which fields an item's own page shows, keyed by type key. `type_label` is a pseudo-field:
    // the type's own name above the title, so choosing it adds no text of the site's.
    { key: 'fields_by_type', type: 'json', default: {}, label: 'Fields on an item’s own page, by type' },
    // `article` is one column in the order `fields_by_type` lists. `case_study` puts the cover or
    // video across the top, the body and long answers on the left, and the short facts and buttons in
    // a column on the right — a project's page.
    { key: 'layout_by_type', type: 'json', default: {}, label: 'Layout of an item’s own page, by type' },
    { key: 'show_related', type: 'bool', default: false, label: 'List the items that point at an item, under its page' },
    // A Page that draws every item of a type, keyed by type key: `{ program: <page id> }`. Its modules
    // read the item as `$context` — a `fields` module draws the item's own fields, a Relationship
    // collection walks from it. A type with no page here is drawn from `fields_by_type` as before.
    { key: 'page_by_type', type: 'json', default: {}, label: 'A page that draws each item of a type' },
    // A heart beside an item's title on its own page: a signed-in visitor saves it to their Arcos
    // account (the canonical Favorited field), and one who is not is asked to sign in first.
    { key: 'favorites', type: 'bool', default: false, label: 'A heart on each item’s page' },
    // What an item's page calls a field, by key, over the field's own name: "Where" for `Location Label`,
    // "Price" for `Price Label` — words for visitors where a field's name was written for the database.
    { key: 'field_labels', type: 'json', default: {}, label: 'What an item’s page calls each field' },
    // A table of contents beside a long body, keyed by type key: `{ document: true }`. It lists the
    // body's headings (src/utils/markdownOutline.js), follows the reader down the page, and gives every
    // heading an address of its own (`/<item>#6-13-shared-abundance`). Shown only on an article-layout
    // page whose body has at least CONTENTS_MIN headings, so a short document is left as it was.
    { key: 'contents_by_type', type: 'json', default: {}, label: 'A table of contents on an item’s page, by type' },
  ],
  // What search engines and link previews are told about the whole site (siteMeta.pageMeta). Each
  // page may say more for itself (PAGE_SEO_SCHEMA); these are what every page falls back on.
  seo: [
    // The picture a shared link shows when the page has none of its own: before the site's logo,
    // after any picture the page itself shows.
    { key: 'share_image', type: 'url', default: '', label: 'Picture when a link is shared' },
    // The browser tab's icon. Empty is the site's logo, as every site had.
    { key: 'favicon', type: 'url', default: '', label: 'Browser tab icon' },
    // The code Google Search Console gives to prove the site is yours, written as its meta tag.
    { key: 'google_verification', type: 'text', max: 100, default: '', label: 'Google Search Console code' },
    // Every page asks search engines to leave it out, and the sitemap lists nothing.
    { key: 'noindex', type: 'bool', default: false, label: 'Keep the whole site out of search results' },
  ],
});

export const PAGE_SCHEMA = Object.freeze([
  { key: 'template', type: 'enum', values: ['landing', 'article'], default: 'auto', auto: 'landing with modules, else article', label: 'Template' },
  { key: 'width', type: 'enum', values: ['narrow', 'normal', 'wide'], default: 'auto', auto: 'narrow for article, else normal', label: 'Width' },
  { key: 'page_header', type: 'enum', values: ['none', 'title', 'title_description'], default: 'auto', auto: 'none for landing, title for article', label: 'Page header' },
  { key: 'chrome', type: 'enum', values: ['site', 'none'], default: 'site', label: 'Site header and footer' },
]);

/**
 * What a page tells search engines and link previews, stored in its frame under `seo`. Every one is
 * empty by default, which means "worked out from the page" (siteMeta.pageMeta), so a page nobody set
 * reads exactly as before.
 */
export const PAGE_SEO_SCHEMA = Object.freeze([
  // The whole title a search result and a browser tab show, in place of "<page> — <site>".
  { key: 'title', type: 'text', max: 120, default: '', label: 'Title in search results' },
  { key: 'description', type: 'text', max: 320, default: '', label: 'Description in search results' },
  { key: 'image', type: 'url', default: '', label: 'Picture when a link is shared' },
  { key: 'noindex', type: 'bool', default: false, label: 'Keep this page out of search results' },
]);

/** A page's SEO settings, complete, from its frame. */
export function readPageSeo(frame) {
  return readFlat(frame && typeof frame === 'object' ? frame.seo : null, PAGE_SEO_SCHEMA);
}

/** Version 1's eight. Image focus, ratio, padding and the rest are added later under rule 1. */
export const SECTION_SCHEMA = Object.freeze([
  { key: 'align', type: 'enum', values: ['left', 'center', 'right'], default: 'left', label: 'Alignment' },
  { key: 'media_position', type: 'enum', values: ['opposite', 'above', 'below'], default: 'opposite', label: 'Image or side text' },
  { key: 'width', type: 'enum', values: ['contained', 'full'], default: 'contained', label: 'Width' },
  { key: 'height', type: 'enum', values: ['auto', 'tall', 'screen'], default: 'auto', label: 'Height' },
  // `gradient`: the primary colour sweeping into the accent, on the diagonal.
  { key: 'tone', type: 'enum', values: ['plain', 'muted', 'primary', 'accent', 'inverse', 'gradient'], default: 'plain', label: 'Tone' },
  // `fade`: the page's own colour at the top, falling away to the whole picture below — words on the
  // page's colour, and a picture that keeps its colour.
  // `fade_bottom` is the same falling the other way: the picture whole at the top, the page's colour below.
  // `gradient`: the picture seen through the primary colour sweeping into the accent — a band with a
  // photograph that still reads as the site's own, its words light.
  { key: 'overlay', type: 'enum', values: ['none', 'light', 'dark', 'fade', 'fade_bottom', 'gradient'], default: 'auto', auto: 'dark over a background, else none', label: 'Background overlay' },
  { key: 'title_size', type: 'enum', values: ['sm', 'md', 'lg', 'xl'], default: 'md', label: 'Title size' },
  { key: 'button_style', type: 'enum', values: ['filled', 'outline', 'text'], default: 'filled', label: 'First button' },
]);

/**
 * The module's own settings that the site reads. `viewType` and `max` already exist on dashboard
 * collection modules; the `isNew` six are what the module system gains for the site.
 */
export const MODULE_SCHEMA = Object.freeze([
  // `video`: cards whose picture is the item's video, played in place — the first click loads the player.
  // `table`: a read-only grid that scrolls inside itself, one row per item and one column per field in
  // `tableFields` — the Airtable-embed look, drawn by the site.
  // `map`: the collection on a map, drawn by the Map View Type's own canvas (src/views/embeddable.js), with
  // the settings of the collection's stored Map View — so a map set up in the hub is the site's map.
  // `feed`: the rows by day under a heading each ("Today", "Tomorrow", "Sat, Oct 18"), each with its time —
  // a phone-first list of what is on, the app's event feed drawn by the site.
  // `browse`: the app's Browse view — a tab per grouping (`groupings`), a card per value with its count and
  // picture, and the rows of the value a visitor opens, drawn as `browseItems`.
  // `marquee`: one row of pills — each a logo or the type's icon beside a name — gliding sideways, for
  // credits: the partners, sources or sponsors behind a site. It holds still for anyone who asks for less motion.
  // `calendar`: a month grid of the rows by the day they start (src/views/calendar), a day's rows listed under it.
  { key: 'viewType', type: 'enum', values: ['cards', 'list', 'video', 'table', 'map', 'feed', 'browse', 'marquee', 'calendar'], default: 'list', label: 'View' },
  { key: 'max', type: 'number', default: 6, label: 'Most items (0 = all)' },
  { key: 'columns', type: 'enum', values: ['auto', '2', '3', '4'], default: 'auto', label: 'Columns in the cards view', isNew: true },
  { key: 'cardImage', type: 'enum', values: ['cover', 'logo'], default: 'auto', auto: 'cover, else logo', label: 'Card image', isNew: true },
  // The colour behind each card's picture. A white icon or logo needs a coloured ground to be seen —
  // Cooperation LI's outline shapes sat on its brand blue in the old site and vanished on the grey.
  // `plain` is the page's own colour, for pictures shot on white (a product, a bottle) that a grey ground
  // would frame as a white box.
  { key: 'cardTone', type: 'enum', values: ['muted', 'primary', 'accent', 'inverse', 'plain'], default: 'muted', label: 'Behind each card’s picture', isNew: true },
  // How a card's cover picture fills its place: cropped to the band, as every card was, or shown whole in
  // a taller place — for upright pictures such as bottles or book covers, which cropping cuts in half.
  // A logo is always shown whole.
  { key: 'cardImageFit', type: 'enum', values: ['crop', 'whole'], default: 'crop', label: 'How a card’s picture fits', isNew: true },
  { key: 'cardClick', type: 'enum', values: ['detail', 'url', 'none'], default: 'auto', auto: 'its page if it has one, else its URL', label: 'Clicking a card', isNew: true },
  // What on a card can be clicked to go where cardClick says: the whole card, or its title alone. Its
  // own buttons and heart keep their own clicks either way, and a video card stays a title so its player
  // plays. The default was `title` until 2026-10-26; a module that wants the title alone now stores it.
  { key: 'cardLinkArea', type: 'enum', values: ['title', 'card'], default: 'card', label: 'What on a card is clickable', isNew: true },
  { key: 'filters', type: 'list', default: [], label: 'Filters', isNew: true },
  // How each filter draws. `pills`: every value a chip in a row of its own, one picked at a time — the
  // look every site had first. `menus`: one chip per filter that opens a menu of its values, several
  // ticked at once, each with its count. `auto`: pills for a filter of up to four values, a menu above.
  { key: 'filterStyle', type: 'enum', values: ['pills', 'menus', 'auto'], default: 'pills', label: 'Filters drawn as', isNew: true },
  { key: 'search', type: 'bool', default: false, label: 'Search box', isNew: true },
  { key: 'cardButtons', type: 'json', default: [], label: 'Buttons from each card’s URL fields', isNew: true },
  // The table's columns, in order, by field key. Empty is worked out from the module (siteModel.tableColumns).
  { key: 'tableFields', type: 'list', default: [], label: 'Table columns', isNew: true },
  // The views a visitor may switch between above the list, e.g. ['table', 'list']. Fewer than two: no switch.
  { key: 'views', type: 'list', default: [], label: 'Views a visitor can switch between', isNew: true },
  // A heart on each card, and a Saved chip that narrows the list to what the visitor saved.
  { key: 'favorites', type: 'bool', default: false, label: 'A heart on each card, and a Saved filter', isNew: true },
  // The details under each title on a card, a list row or a feed row, in order, by field key: a date, a
  // place, a price. A relationship reads as the titles of what it names, and the start date as a date.
  { key: 'metaFields', type: 'list', default: [], label: 'Details under each title', isNew: true },
  // A count on each card: `{ collection, filters, label, one }`. `collection` is a collection walked from the
  // card's item, or `$self` when the card's item is itself a collection (a style's calendar); `filters`, in
  // a stored query's shape, narrow what is counted ("upcoming": Date Start on or after $today). A count of
  // none is not shown: "0 upcoming events" on a card says nothing the missing label does not.
  { key: 'cardCount', type: 'json', default: {}, label: 'A count on each card', isNew: true },
  // The words this module uses for a field, by key, over the field's own name: a relationship read from
  // its other end ("Venue" for the venue an event is held at) or a key with no friendly name.
  { key: 'fieldLabels', type: 'json', default: {}, label: 'What this section calls each field', isNew: true },
  // Live numbers in a strip under the section's words: `[{ collection, filters, distinct, label, one,
  // itemId | href }]`, each counting a collection's rows that pass `filters`, or the different values of
  // `distinct` across them, as the page loads (siteModel.statValue).
  { key: 'stats', type: 'json', default: [], label: 'Live numbers', isNew: true },
  // A live picture beside the words, in place of `image`: `{ kind: 'week', collection, label, from, field }`
  // draws the next days of the collection's real events arriving in a calendar card (SiteWeekArt.jsx).
  { key: 'media', type: 'json', default: {}, label: 'A live picture', isNew: true },
  // Browse's tabs, by field key, in order. Empty takes `filters`.
  { key: 'groupings', type: 'list', default: [], label: 'Browse by', isNew: true },
  // How Browse draws the rows of the value a visitor opens.
  { key: 'browseItems', type: 'enum', values: ['cards', 'list', 'feed'], default: 'cards', label: 'Browse shows each value’s items as', isNew: true },
  // A "Sync to Google Calendar" button beside the module's search and filters: for the module's own
  // collection, or for the item a type's page is drawing when that item is itself a collection (a style).
  { key: 'sync', type: 'enum', values: ['none', 'collection', 'context'], default: 'none', label: 'A button to sync to Google Calendar', isNew: true },
  // A `fields` module — the item a type's page is drawing. `fields` lists which of its fields, in order;
  // empty takes the site's list for the type (`detail.fields_by_type`). `layout` is `article` or
  // `case_study`; `auto` takes the site's layout for the type (`detail.layout_by_type`).
  { key: 'fields', type: 'list', default: [], label: 'The item’s fields, in order', isNew: true },
  { key: 'layout', type: 'enum', values: ['article', 'case_study'], default: 'auto', auto: 'the site’s layout for the type', label: 'Layout', isNew: true },
]);

/** Is `value` allowed for the schema entry `entry`? Absent counts as not allowed. */
export function isValidSetting(entry, value) {
  if (value === undefined || value === null) return false;
  switch (entry.type) {
    case 'enum': return entry.values.includes(String(value));
    case 'bool': return typeof value === 'boolean';
    case 'number': return Number.isFinite(value) && value >= 0;
    case 'color': return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);
    case 'list': return Array.isArray(value) && value.every((x) => typeof x === 'string');
    case 'json': return typeof value === 'object';
    // Words: a value that is only spaces is no value.
    case 'text': return typeof value === 'string' && value.trim().length > 0 && value.length <= (entry.max || 500);
    // A picture or icon: a web address, or a path on the site's own host.
    case 'url': return typeof value === 'string' && /^(https?:\/\/[^\s]+|\/[^\s]*)$/i.test(value);
    default: return false;
  }
}

/** A complete flat set of settings: every key present, every value valid. Never throws. */
export function readFlat(stored, entries) {
  const src = stored && typeof stored === 'object' ? stored : {};
  const out = {};
  for (const e of entries) out[e.key] = isValidSetting(e, src[e.key]) ? src[e.key] : e.default;
  return out;
}

/** A complete grouped set, for the site's settings. */
export function readGrouped(stored, groups) {
  const src = stored && typeof stored === 'object' ? stored : {};
  const out = {};
  for (const [g, entries] of Object.entries(groups)) out[g] = readFlat(src[g], entries);
  return out;
}

/**
 * The stored object after setting one key: the value when it is valid and differs from the
 * default, nothing when it equals the default or is `undefined` (which is how a reset is asked
 * for), and the object unchanged when the value is invalid. Returns a new object.
 */
export function patchFlat(stored, entries, key, value) {
  const entry = entries.find((e) => e.key === key);
  const next = { ...(stored && typeof stored === 'object' ? stored : {}) };
  if (!entry) return next;
  if (value === undefined || JSON.stringify(value) === JSON.stringify(entry.default)) delete next[key];
  else if (isValidSetting(entry, value)) next[key] = value;
  return next;
}

// ─── Readable colour ────────────────────────────────────────────────────────────────────────
//
// A site brings its own brand colour, and most brand colours do not carry text well: cooperationli's
// #5578eb gives white text 3.98:1, under the 4.5:1 WCAG AA asks of body-size text. With hundreds of
// sites nobody will hand-pick a header shade for each, so the renderer works it out: pick the text
// colour that reads best on the brand colour, then move the colour BEHIND that text — never the text
// itself — toward black or white just far enough to reach the target. The brand colour stays exact
// wherever no text sits on it (accents, borders, eyebrows).

const hexToRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const rgbToHex = (rgb) => `#${rgb.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('')}`;
const luminance = ([r, g, b]) => {
  const f = (v) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

/** WCAG contrast ratio between two hex colours, 1–21. */
export function contrastRatio(hexA, hexB) {
  const a = hexToRgb(hexA); const b = hexToRgb(hexB);
  if (!a || !b) return 1;
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** `base` moved toward black (0) or white (255) in 2% steps until `text` on it reaches `min`. */
function shiftUntilReadable(base, text, toward, min, maxShift) {
  for (let step = 0; step * 0.02 <= maxShift + 1e-9; step++) {
    const fill = rgbToHex(base.map((v) => v + (toward - v) * step * 0.02));
    if (contrastRatio(text, fill) >= min) return { fill, shift: step * 0.02 };
  }
  return null;
}

/**
 * The pair to use wherever text sits on a brand colour: `{ fill, text }`.
 *
 * White text is preferred — it is what a saturated brand header almost always carries — whenever
 * deepening the colour by at most 30% gets it to `min`. Otherwise (a yellow, a pastel) the text is
 * `ink` and the colour is lightened instead. A colour that already reads is returned unchanged.
 */
export function readablePair(hex, ink = '#1d1d27', min = 4.5) {
  const base = hexToRgb(hex);
  if (!base) return { fill: hex, text: '#ffffff' };
  const white = shiftUntilReadable(base, '#ffffff', 0, min, 0.3);
  if (white) return { fill: white.fill, text: '#ffffff' };
  const dark = shiftUntilReadable(base, ink, 255, min, 0.8);
  if (dark) return { fill: dark.fill, text: ink };
  return { fill: rgbToHex(base.map((v) => v * 0.4)), text: '#ffffff' };
}

/**
 * Whether the footer is dark, so what sits on it — the "Built with Arcos" mark — is drawn light.
 * `theme` is the read site theme (an `auto` accent is the primary).
 */
export function footerIsDark(theme, tone) {
  if (tone === 'plain') return !isLightHex(theme.surface);
  if (tone === 'primary' || tone === 'accent') {
    const hex = tone === 'accent' && theme.accent !== 'auto' ? theme.accent : theme.primary;
    return readablePair(hex, theme.ink).text === '#ffffff';
  }
  return !isLightHex(theme.ink);
}
const isLightHex = (hex) => contrastRatio(hex, '#000000') > contrastRatio(hex, '#ffffff');

/**
 * The brand colour used AS text on `surface` (an eyebrow, a label): moved toward black on a light
 * surface, or white on a dark one, until it reaches `min` against it.
 */
export function readableText(hex, surface = '#ffffff', min = 4.5) {
  const base = hexToRgb(hex);
  if (!base) return hex;
  const lightSurface = contrastRatio('#000000', surface) >= contrastRatio('#ffffff', surface);
  const res = shiftUntilReadable(base, surface, lightSurface ? 0 : 255, min, 0.8);
  return res ? res.fill : (lightSurface ? '#000000' : '#ffffff');
}

/** The site's `custom_fields.Apps` entry, or null when the item is not a site. */
export function siteBinding(customFields) {
  let apps = customFields?.Apps;
  if (typeof apps === 'string') {
    try { apps = JSON.parse(apps); } catch { apps = null; }
  }
  if (!Array.isArray(apps)) return null;
  return apps.find((a) => a && a.key === 'site') || null;
}

/** The site's settings, complete. */
export function readSiteSettings(customFields) {
  return readGrouped(siteBinding(customFields)?.config, SITE_SCHEMA);
}
